import { useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import {
    Box,
    Button,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";

import {
    Analytics,
    Assignment,
    Build,
    Close,
    Computer,
    Dashboard,
    ExpandLess,
    ExpandMore,
    Inventory2,
    People,
    ReceiptLong,
    ConfirmationNumber,
    DirectionsBike,
    DirectionsCar,
    PointOfSale,
    ShoppingCart,
    Sync,
    Tune,
    FactCheck,
    AccountBalanceWallet,
    UploadFile,
    History,
    KeyboardArrowDown,
} from "@mui/icons-material";

import { AuthContext } from "../auth/AuthContext";
import { ROUTE_PRELOADERS } from "../routes/pageLoaders";
import { POS_INVENTORY_ROLES, POS_OPERATION_ROLES, POS_ROLES } from "../modules/pos/constants";
import { FLEET_ADMIN_ROLES, FLEET_TECH_ROLES } from "../modules/fleet/constants";
import { MCO_ADMIN_ROLES, MCO_ENABLED, MCO_ROLES } from "../modules/mco/constants";

const DRAWER_WIDTH = 260;
const TOPBAR_HEIGHT = 68;

const NAV_GROUPS = [
    {
        id: "overview",
        label: "Overview",
        source: "TERMINAL",
        ungrouped: true,
        items: [
            {
                label: "Dashboard",
                path: "/dashboard",
                icon: <Dashboard />,
                roles: ["SUPER_ADMIN", "OPERATIONS_MANAGER", "INVENTORY_ADMIN"],
            },
        ],
    },
    {
        id: "inventory",
        label: "Inventory Management",
        source: "TERMINAL",
        items: [
            {
                label: "Products",
                path: "/products",
                icon: <Inventory2 />,
                roles: ["SUPER_ADMIN", "INVENTORY_ADMIN"],
            },
            {
                label: "Purchases",
                path: "/purchases",
                icon: <ShoppingCart />,
                roles: ["SUPER_ADMIN", "INVENTORY_ADMIN", "OPERATIONS_MANAGER", "OFFICE_TECH"],
            },
            {
                label: "Inventory",
                path: "/inventory",
                icon: <Tune />,
                roles: ["SUPER_ADMIN", "INVENTORY_ADMIN", "OPERATIONS_MANAGER", "OFFICE_TECH"],
            },
            {
                label: "Change Approvals",
                path: "/change-approvals",
                icon: <ReceiptLong />,
                roles: ["SUPER_ADMIN", "OPERATIONS_MANAGER", "INVENTORY_ADMIN", "OFFICE_TECH"],
            },
            {
                label: "Pending Returns",
                path: "/old-pending",
                icon: <ReceiptLong />,
                roles: ["SUPER_ADMIN", "OPERATIONS_MANAGER", "INVENTORY_ADMIN", "OFFICE_TECH"],
            },
            {
                label: "Repair & Warranty",
                path: "/repair-warranty",
                icon: <Build />,
                roles: ["SUPER_ADMIN", "INVENTORY_ADMIN", "OFFICE_TECH"],
            },
        ],
    },
    {
        id: "pos-dashboard",
        label: "POS Dashboard",
        source: "POS",
        ungrouped: true,
        items: [
            {
                label: "POS Dashboard",
                path: "/pos/dashboard",
                icon: <PointOfSale />,
                roles: POS_ROLES,
            },
        ],
    },
    {
        id: "pos-operations",
        label: "POS Operations",
        source: "POS",
        items: [
            {
                label: "POS Tickets",
                path: "/pos/tickets",
                icon: <ConfirmationNumber />,
                roles: POS_ROLES,
            },
            {
                label: "Repairs",
                path: "/pos/repairs",
                icon: <Build />,
                roles: POS_OPERATION_ROLES,
            },
            {
                label: "Device Replacements",
                path: "/pos/device-replacements",
                icon: <Sync />,
                roles: POS_OPERATION_ROLES,
            },
            {
                label: "Serial Changes",
                path: "/pos/serial-changes",
                icon: <ReceiptLong />,
                roles: POS_ROLES,
            },
        ],
    },
    {
        id: "pos-inventory",
        label: "POS Inventory",
        source: "POS",
        items: [
            {
                label: "POS Products",
                path: "/pos/products",
                icon: <Inventory2 />,
                roles: POS_INVENTORY_ROLES,
            },
            {
                label: "POS Inventory",
                path: "/pos/inventory",
                icon: <Inventory2 />,
                roles: POS_INVENTORY_ROLES,
            },
            {
                label: "Inventory Movements",
                path: "/pos/inventory-movements",
                icon: <Sync />,
                roles: ["SUPER_ADMIN"],
            },
            {
                label: "POS Purchases",
                path: "/pos/purchases",
                icon: <ShoppingCart />,
                roles: POS_INVENTORY_ROLES,
            },
            {
                label: "Used Parts",
                path: "/pos/used-parts",
                icon: <FactCheck />,
                roles: POS_INVENTORY_ROLES,
            },
        ],
    },
    {
        id: "fleet-dashboard",
        label: "Fleet Dashboard",
        source: "FLEET",
        ungrouped: true,
        items: [
            {
                label: "Fleet Dashboard",
                path: "/fleet/dashboard",
                icon: <DirectionsCar />,
                roles: FLEET_ADMIN_ROLES,
            },
        ],
    },
    {
        id: "fleet-management",
        label: "Fleet Management",
        source: "FLEET",
        items: [
            {
                label: "Vehicles",
                path: "/fleet/vehicles",
                icon: <DirectionsCar />,
                roles: FLEET_ADMIN_ROLES,
            },
            {
                label: "Assignments",
                path: "/fleet/assignments",
                icon: <Assignment />,
                roles: FLEET_ADMIN_ROLES,
            },
            {
                label: "Usage Logs",
                path: "/fleet/usage-logs",
                icon: <ReceiptLong />,
                roles: FLEET_ADMIN_ROLES,
            },
            {
                label: "Fuel Records",
                path: "/fleet/fuel-records",
                icon: <Tune />,
                roles: FLEET_ADMIN_ROLES,
            },
            {
                label: "Maintenance",
                path: "/fleet/maintenance",
                icon: <Build />,
                roles: FLEET_ADMIN_ROLES,
            },
            {
                label: "Fleet Reports",
                path: "/fleet/reports",
                icon: <Analytics />,
                roles: FLEET_ADMIN_ROLES,
            },
        ],
    },
    {
        id: "fleet-tech",
        label: "My Fleet",
        source: "FLEET",
        items: [
            {
                label: "My Vehicles",
                path: "/fleet/my-vehicles",
                icon: <DirectionsBike />,
                roles: FLEET_TECH_ROLES,
            },
            {
                label: "My Mileage",
                path: "/fleet/my-usage-logs",
                icon: <ReceiptLong />,
                roles: FLEET_TECH_ROLES,
            },
            {
                label: "My Fuel",
                path: "/fleet/my-fuel-records",
                icon: <Tune />,
                roles: FLEET_TECH_ROLES,
            },
        ],
    },
    {
        id: "mco-dashboard",
        label: "MCO Dashboard",
        source: "MCO",
        ungrouped: true,
        enabled: MCO_ENABLED,
        items: [
            {
                label: "MCO Dashboard",
                path: "/mco/dashboard",
                icon: <AccountBalanceWallet />,
                roles: MCO_ROLES,
            },
        ],
    },
    {
        id: "mco-operations",
        label: "Cash Operations",
        source: "MCO",
        enabled: MCO_ENABLED,
        items: [
            {
                label: "Batches",
                path: "/mco/batches",
                icon: <Assignment />,
                roles: MCO_ROLES,
            },
            {
                label: "Assignments",
                path: "/mco/assignments",
                icon: <ReceiptLong />,
                roles: MCO_ROLES,
            },
            {
                label: "Pending",
                path: "/mco/pending-assignments",
                icon: <FactCheck />,
                roles: MCO_ROLES,
            },
            {
                label: "Assignments Report",
                path: "/mco/daily-totals",
                icon: <Analytics />,
                roles: MCO_ROLES,
            },
            {
                label: "Terminals",
                path: "/mco/terminals",
                icon: <Computer />,
                roles: MCO_ROLES,
            },
            {
                label: "Mismatches",
                path: "/mco/mismatches",
                icon: <Sync />,
                roles: MCO_ROLES,
            },
            {
                label: "Sitrek Reports",
                path: "/mco/sitrek",
                icon: <UploadFile />,
                roles: MCO_ROLES,
            },
        ],
    },
    {
        id: "mco-admin",
        label: "MCO Administration",
        source: "MCO",
        enabled: MCO_ENABLED,
        items: [
            {
                label: "Settings",
                path: "/mco/settings",
                icon: <Tune />,
                roles: MCO_ADMIN_ROLES,
            },
            {
                label: "System",
                path: "/mco/system",
                icon: <Sync />,
                roles: MCO_ADMIN_ROLES,
            },
            {
                label: "Audit Log",
                path: "/mco/audit",
                icon: <History />,
                roles: MCO_ADMIN_ROLES,
            },
        ],
    },
    {
        id: "operations",
        label: "Operations",
        source: "TERMINAL",
        items: [
            {
                label: "Assign Parts",
                path: "/assign-parts",
                icon: <Inventory2 />,
                roles: ["SUPER_ADMIN", "INVENTORY_ADMIN", "OFFICE_TECH"],
            },
            {
                label: "Tasks",
                path: "/tasks",
                icon: <Assignment />,
                roles: ["SUPER_ADMIN", "OPERATIONS_MANAGER", "FIELD_TECH", "OFFICE_TECH", "INVENTORY_ADMIN"],
            },
            {
                label: "MegaPay Tickets",
                path: "/megapay-tickets",
                icon: <ConfirmationNumber />,
                roles: ["SUPER_ADMIN", "OPERATIONS_MANAGER", "FIELD_TECH", "OFFICE_TECH", "INVENTORY_ADMIN"],
            },
            {
                label: "Offline Terminals",
                path: "/offline-terminals",
                icon: <Computer />,
                roles: ["SUPER_ADMIN", "OPERATIONS_MANAGER"],
            },
            {
                label: "Sold Parts",
                path: "/sold-parts",
                icon: <PointOfSale />,
                roles: ["SUPER_ADMIN", "OPERATIONS_MANAGER", "INVENTORY_ADMIN"],
            },
            {
                label: "My Parts",
                path: "/my-parts",
                icon: <Inventory2 />,
                roles: ["FIELD_TECH"],
            },
        ],
    },
    {
        id: "analytics",
        label: "Analytics & Reports",
        source: "TERMINAL",
        items: [
            {
                label: "Terminal Health",
                path: "/reports",
                icon: <Analytics />,
                roles: ["SUPER_ADMIN", "OPERATIONS_MANAGER"],
            },
            {
                label: "Technician Performance",
                path: "/technician-performance",
                icon: <Assignment />,
                roles: ["SUPER_ADMIN", "OPERATIONS_MANAGER"],
            },
            {
                label: "Terminal Performance",
                path: "/terminal-performance",
                icon: <Analytics />,
                roles: ["SUPER_ADMIN"],
            },
            {
                label: "Parts Movement",
                path: "/parts-movement",
                icon: <Inventory2 />,
                roles: ["SUPER_ADMIN"],
            },
        ],
    },
    {
        id: "terminals",
        label: "Terminal Management",
        source: "TERMINAL",
        items: [
            {
                label: "Terminals",
                path: "/terminals",
                icon: <Computer />,
                roles: ["SUPER_ADMIN", "OPERATIONS_MANAGER", "INVENTORY_ADMIN", "OFFICE_TECH", "FIELD_TECH"],
            },
        ],
    },
    {
        id: "admin",
        label: "Administration",
        source: "TERMINAL",
        items: [
            {
                label: "User Management",
                path: "/settings-users",
                icon: <People />,
                roles: ["SUPER_ADMIN"],
            },
            {
                label: "Assignment Sync",
                path: "/admin/megapay-assignment-sync",
                icon: <Sync />,
                roles: ["SUPER_ADMIN"],
            },
            {
                label: "Status Sync",
                path: "/admin/megapay-status-sync",
                icon: <Sync />,
                roles: ["SUPER_ADMIN"],
            },
            {
                label: "MegaPay Settings",
                path: "/admin/megapay-settings",
                icon: <Tune />,
                roles: ["SUPER_ADMIN"],
            },
            {
                label: "Part Prices",
                path: "/admin/part-prices",
                icon: <ReceiptLong />,
                roles: ["SUPER_ADMIN"],
            },
        ],
    },
];

export default function Sidebar({ mobileOpen, setMobileOpen }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const role = user?.role;
    const activeSource = location.pathname.startsWith("/pos")
        ? "POS"
        : location.pathname.startsWith("/fleet")
            ? "FLEET"
            : location.pathname.startsWith("/mco")
                ? "MCO"
                : "TERMINAL";
    const [sourceAnchorEl, setSourceAnchorEl] = useState(null);

    const visibleGroups = useMemo(() => {
        const canAccess = (roles) => !roles || roles.includes(role);

        return NAV_GROUPS.filter(
            (group) => group.enabled !== false && (group.alwaysVisible || !group.source || group.source === activeSource)
        )
            .map((group) => ({
                ...group,
                items: group.items.filter((item) => canAccess(item.roles)),
            }))
            .filter((group) => group.items.length > 0);
    }, [activeSource, role]);

    const activeGroupIds = useMemo(
        () =>
            visibleGroups
                .filter((group) =>
                    group.items.some((item) => location.pathname === item.path)
                )
                .map((group) => group.id),
        [location.pathname, visibleGroups]
    );

    const [openGroups, setOpenGroups] = useState({});

    useEffect(() => {
        setOpenGroups((prev) => {
            const next = { ...prev };
            activeGroupIds.forEach((groupId) => {
                next[groupId] = true;
            });
            return next;
        });
    }, [activeGroupIds]);

    if (!user) return null;

    const isItemActive = (path) => location.pathname === path;
    const canAccess = (roles) => !roles || roles.includes(role);
    const sourceOptions = [
        {
            value: "TERMINAL",
            label: "Terminal",
            path: role === "FIELD_TECH" ? "/tasks" : role === "OFFICE_TECH" ? "/repair-warranty" : "/dashboard",
            icon: <Dashboard fontSize="small" />,
            roles: ["SUPER_ADMIN", "OPERATIONS_MANAGER", "INVENTORY_ADMIN", "FIELD_TECH", "OFFICE_TECH"],
        },
        {
            value: "POS",
            label: "POS",
            path: "/pos/dashboard",
            icon: <PointOfSale fontSize="small" />,
            roles: POS_ROLES,
        },
        {
            value: "FLEET",
            label: "Fleet",
            path: role === "FIELD_TECH" ? "/fleet/my-vehicles" : "/fleet/dashboard",
            icon: <DirectionsCar fontSize="small" />,
            roles: [...FLEET_ADMIN_ROLES, ...FLEET_TECH_ROLES],
        },
        {
            value: "MCO",
            label: "MegaPay Cash Ops",
            path: "/mco/dashboard",
            icon: <AccountBalanceWallet fontSize="small" />,
            roles: MCO_ROLES,
            enabled: MCO_ENABLED,
        },
    ].filter((option) => option.enabled !== false && canAccess(option.roles));

    const isGroupActive = (group) =>
        group.items.some((item) => isItemActive(item.path));

    const handleNavItemClick = () => {
        if (isMobile) setMobileOpen(false);
    };

    const selectSource = (option) => {
        setSourceAnchorEl(null);
        navigate(option.path);
        if (isMobile) setMobileOpen(false);
    };

    const preloadRoute = (path) => {
        const preload = ROUTE_PRELOADERS[path];
        if (preload) {
            preload();
        }
    };

    const toggleGroup = (groupId) => {
        setOpenGroups((prev) => ({
            ...prev,
            [groupId]: !prev[groupId],
        }));
    };

    const renderNavItem = (item) => {
        const active = isItemActive(item.path);

        return (
            <ListItemButton
                key={item.path}
                component={RouterLink}
                to={item.path}
                selected={active}
                onClick={handleNavItemClick}
                onMouseEnter={() => preloadRoute(item.path)}
                onFocus={() => preloadRoute(item.path)}
                onTouchStart={() => preloadRoute(item.path)}
                sx={{
                    mx: 1.25,
                    my: 0.35,
                    minHeight: 42,
                    borderRadius: 2.25,
                    px: 1.75,
                    py: 0.85,
                    color: active ? "primary.main" : "text.secondary",
                    transition: "all 0.18s ease",
                    "& .MuiListItemIcon-root": {
                        color: active ? "primary.main" : "text.secondary",
                    },
                    "&.Mui-selected": {
                        backgroundColor: "rgba(37, 99, 235, 0.1)",
                        color: "primary.main",
                    },
                    "&.Mui-selected:hover": {
                        backgroundColor: "rgba(37, 99, 235, 0.14)",
                    },
                    "&:hover": {
                        backgroundColor: "rgba(15, 23, 42, 0.05)",
                        color: "text.primary",
                    },
                }}
            >
                <ListItemIcon sx={{ minWidth: 34 }}>
                    {item.icon}
                </ListItemIcon>
                <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                        fontSize: 13.5,
                        fontWeight: active ? 800 : 650,
                        letterSpacing: "-0.01em",
                    }}
                />
            </ListItemButton>
        );
    };

    const renderGroup = (group) => {
        if (group.ungrouped) {
            return (
                <Box key={group.id} sx={{ mb: 0.75 }}>
                    {group.items.map(renderNavItem)}
                </Box>
            );
        }

        const open = openGroups[group.id] ?? isGroupActive(group);
        const active = isGroupActive(group);

        return (
            <Box key={group.id} sx={{ mb: 0.75 }}>
                <ListItemButton
                    onClick={() => toggleGroup(group.id)}
                    selected={active}
                    sx={{
                        mx: 1.25,
                        mt: 0.75,
                        mb: 0.35,
                        minHeight: 38,
                        borderRadius: 2.25,
                        px: 1.75,
                        py: 0.65,
                        color: active ? "primary.main" : "text.secondary",
                        "&.Mui-selected": {
                            backgroundColor: "rgba(37, 99, 235, 0.08)",
                        },
                        "&.Mui-selected:hover": {
                            backgroundColor: "rgba(37, 99, 235, 0.12)",
                        },
                        "&:hover": {
                            backgroundColor: "rgba(15, 23, 42, 0.04)",
                        },
                    }}
                >
                    <ListItemText
                        primary={group.label}
                        primaryTypographyProps={{
                            fontSize: 11.5,
                            fontWeight: 900,
                            letterSpacing: 0.75,
                            textTransform: "uppercase",
                        }}
                    />
                    {open ? (
                        <ExpandLess fontSize="small" />
                    ) : (
                        <ExpandMore fontSize="small" />
                    )}
                </ListItemButton>

                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List disablePadding>
                        {group.items.map(renderNavItem)}
                    </List>
                </Collapse>
            </Box>
        );
    };

    return (
        <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            open={isMobile ? mobileOpen : true}
            onClose={() => setMobileOpen(false)}
            ModalProps={{
                keepMounted: true,
                BackdropProps: {
                    sx: { backgroundColor: "rgba(15, 23, 42, 0.32)" },
                },
            }}
            sx={{
                width: DRAWER_WIDTH,
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                    width: DRAWER_WIDTH,
                    boxSizing: "border-box",
                    top: isMobile ? 0 : `${TOPBAR_HEIGHT}px`,
                    height: isMobile ? "100%" : `calc(100% - ${TOPBAR_HEIGHT}px)`,
                    borderRight: "1px solid rgba(148, 163, 184, 0.2)",
                    backgroundColor: "rgba(255, 255, 255, 0.98)",
                    boxShadow: isMobile ? "0 24px 60px rgba(15,23,42,0.18)" : "none",
                    overflowX: "hidden",
                },
            }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={2}
                py={isMobile ? 1.5 : 1.25}
            >
                <Box>
                    <Typography
                        variant="overline"
                        sx={{
                            color: "text.secondary",
                            fontWeight: 900,
                            letterSpacing: 1,
                        }}
                    >
                        Navigation
                    </Typography>
                    {isMobile && (
                        <Typography variant="body2" color="text.secondary">
                            Select a page to continue.
                        </Typography>
                    )}
                </Box>

                {isMobile && (
                    <IconButton
                        onClick={() => setMobileOpen(false)}
                        aria-label="Close navigation"
                        sx={{
                            border: "1px solid rgba(148, 163, 184, 0.22)",
                            borderRadius: 2,
                        }}
                    >
                        <Close />
                    </IconButton>
                )}
            </Box>

            <Divider sx={{ borderColor: "rgba(148, 163, 184, 0.16)" }} />

            <Box px={2} pt={1.25} pb={0.75}>
                <Typography
                    variant="overline"
                    sx={{
                        color: "text.secondary",
                        fontWeight: 900,
                        letterSpacing: 1,
                    }}
                >
                    Source
                </Typography>
                <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={(event) => setSourceAnchorEl(event.currentTarget)}
                    endIcon={<KeyboardArrowDown />}
                    sx={{
                        justifyContent: "space-between",
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 800,
                    }}
                    disabled={sourceOptions.length <= 1}
                >
                    {activeSource === "POS" ? "POS" : activeSource === "FLEET" ? "Fleet" : activeSource === "MCO" ? "MegaPay Cash Ops" : "Terminal"}
                </Button>
                <Divider sx={{ mt: 1, borderColor: "rgba(148, 163, 184, 0.16)" }} />
                <Popover
                    open={Boolean(sourceAnchorEl)}
                    anchorEl={sourceAnchorEl}
                    onClose={() => setSourceAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{
                        sx: {
                            mt: 0.75,
                            width: 210,
                            p: 0.75,
                            borderRadius: 2,
                            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.16)",
                        },
                    }}
                >
                    {sourceOptions.map((option) => (
                        <ListItemButton
                            key={option.value}
                            selected={activeSource === option.value}
                            onClick={() => selectSource(option)}
                            sx={{ borderRadius: 1.5 }}
                        >
                            <ListItemIcon sx={{ minWidth: 34 }}>{option.icon}</ListItemIcon>
                            <ListItemText
                                primary={option.label}
                                primaryTypographyProps={{ fontSize: 13.5, fontWeight: 800 }}
                            />
                        </ListItemButton>
                    ))}
                </Popover>
            </Box>

            <List
                component="nav"
                sx={{
                    py: 1.25,
                    pb: 3,
                    overflowY: "auto",
                }}
            >
                {visibleGroups.map(renderGroup)}
            </List>
        </Drawer>
    );
}
